import {z as r} from 'zod';

import {isRecord} from '../../../util/object';

/**
 * Admin help link: urlPattern = ^feature-flag-review-realtime$
 *
 * Pilot (`counterpartyIds` in admin = brand ids on frontend):
 * { "counterpartyIds": [123, 456], "staffCanSend": false }
 *
 * Rollout for all:
 * { "available": true, "staffCanSend": false }
 */
function coerceIdArray(value: unknown): Array<number> | null {
    if (Array.isArray(value)) {
        return value.filter((item): item is number => typeof item === 'number');
    }

    if (typeof value !== 'string') {
        return null;
    }

    try {
        const parsed: unknown = JSON.parse(value);

        if (!Array.isArray(parsed)) {
            return null;
        }

        return parsed.filter((item): item is number => typeof item === 'number');
    } catch {
        return null;
    }
}

function normalizeReviewRealtimeHelpLinkConfigInput(config: unknown): unknown {
    if (!isRecord(config)) {
        return config;
    }

    const counterpartyIds = coerceIdArray(config.counterpartyIds);

    return {
        ...config,
        ...(counterpartyIds ? {counterpartyIds} : {}),
        available: config.available,
        staffCanSend: config.staffCanSend,
    };
}

const reviewRealtimeHelpLinkConfigSchema = r.preprocess(
    normalizeReviewRealtimeHelpLinkConfigInput,
    r
        .object({
            available: r.boolean().optional(),
            counterpartyIds: r.array(r.number()).optional(),
            staffCanSend: r.boolean().optional(),
        })
        .transform((data) => ({
            available: data.available,
            staffCanSend: data.staffCanSend,
            brandIds: data.counterpartyIds ?? [],
        }))
        .refine((data) => data.available === true || data.brandIds.length > 0, {
            message: 'Either available:true or non-empty counterpartyIds is required',
        })
);

export type ReviewRealtimeHelpLinkConfigType = r.infer<typeof reviewRealtimeHelpLinkConfigSchema>;

export function parseReviewRealtimeHelpLinkConfig(config: unknown): ReviewRealtimeHelpLinkConfigType | null {
    const parseResult = reviewRealtimeHelpLinkConfigSchema.safeParse(config);

    if (!parseResult.success) {
        return null;
    }

    return parseResult.data;
}
